import {
  COLUMN_MARKER,
  CURRENT_URL_MARKER,
  LINE_MARKER,
  ON_ERROR_API,
  ON_REJECTION_API,
  REJECTION_STACK_MARKER,
  REPEAT_COUNT_MARKER,
  SCRIPT_MARKER,
  STACK_MARKER,
} from '../utilities/error-message-markers';
import type { InstrumentationLog } from '../types';

/**
 * Classification values written to the `clienterr.FailKind` column so dashboards can exclude
 * known-benign rows in a single predicate instead of string-matching `Mes`.
 */
export const FailKinds = {
  benignResizeObserver: 'benign-resizeobserver',
  crossOriginScriptError: 'xorigin-script-error',
};

// `clienterr.Stack` is a supplementary column - the full frames remain in `Mes`. Truncating keeps
// the 1DS event well under its payload limit, matching the mkt-landing telemetry precedent.
const MAX_STACK_LENGTH = 500;

const DIGITS_ONLY = /^\d+$/;
const LEADING_NOISE = /^(?:Uncaught\s+)?(?:[A-Za-z]*Error:\s+)?/;
const RESIZE_OBSERVER_LOOP = /^ResizeObserver loop/;
const CROSS_ORIGIN_MESSAGE = /^Script error\.?$/;

// Tokens can appear in script URLs and in stack frame file names. `getPFlagsProps` already scrubs
// these out of `api`; `Source`/`Stack` need the same treatment before they become queryable columns.
const SENSITIVE_PARAMS = /(authCode|accessToken|refreshToken|LongLivedAccessToken)=[^&\s"')]*/gi;

function scrubTokens(value: string): string {
  return value.replace(SENSITIVE_PARAMS, (_match, name) => `${name}=REDACTED`);
}

/**
 * The message head is everything the browser reported, before ErrorStub appended its own
 * tab-delimited `Script:` / `CurrentURL:` segments.
 * @param {string} message - the flattened error message
 * @returns {string} the browser-supplied portion of the message
 */
function getMessageHead(message: string): string {
  const tabIndex = message.indexOf('\t');
  return tabIndex === -1 ? message : message.substring(0, tabIndex);
}

function toPositiveInteger(value: string): number | undefined {
  return DIGITS_ONLY.test(value) ? Number(value) : undefined;
}

/**
 * Extracts the `Script: <url> Line: <n> Column: <n> StackTrace: <frames>` segment that ErrorStub
 * appends to `window.onerror` messages.
 *
 * Anchors on the LAST marker before `CurrentURL:` rather than searching the whole string, because
 * the browser-supplied message can itself contain `Script:` or ` Line: ` text - an unanchored
 * search would then extract the wrong location and report it as authoritative.
 * @param {string} message - the flattened error message
 * @returns {string|undefined} the script segment, or undefined when the message is not in that shape
 */
function getScriptSegment(message: string): string | undefined {
  const currentUrlIndex = message.lastIndexOf(CURRENT_URL_MARKER);
  const searchable = currentUrlIndex === -1 ? message : message.substring(0, currentUrlIndex);
  const scriptIndex = searchable.lastIndexOf(SCRIPT_MARKER);

  if (scriptIndex === -1) {
    return undefined;
  }

  return searchable.substring(scriptIndex + SCRIPT_MARKER.length);
}

interface ParsedLocation {
  source: string;
  line?: number;
  col?: number;
  stack: string;
}

function parseScriptSegment(segment: string): ParsedLocation {
  let remainder = segment;
  let stack = '';

  const stackIndex = remainder.indexOf(STACK_MARKER);
  if (stackIndex !== -1) {
    stack = remainder.substring(stackIndex + STACK_MARKER.length);
    remainder = remainder.substring(0, stackIndex);
  }

  let col;
  const columnIndex = remainder.lastIndexOf(COLUMN_MARKER);
  if (columnIndex !== -1) {
    col = toPositiveInteger(remainder.substring(columnIndex + COLUMN_MARKER.length).trim());
    remainder = remainder.substring(0, columnIndex);
  }

  let line;
  const lineIndex = remainder.lastIndexOf(LINE_MARKER);
  if (lineIndex !== -1) {
    line = toPositiveInteger(remainder.substring(lineIndex + LINE_MARKER.length).trim());
    remainder = remainder.substring(0, lineIndex);
  }

  return {
    source: remainder.trim(),
    line,
    col,
    stack: stack.trim(),
  };
}

function isCrossOriginError(head: string, location?: ParsedLocation): boolean {
  if (CROSS_ORIGIN_MESSAGE.test(head.trim())) {
    return true;
  }

  // A cross-origin failure is reported with no script, line or column. Emitting the literal
  // `Line: 0` the browser supplies would look like a real frame, so treat it as no location at all.
  return !!location && !location.source && !location.line && !location.col;
}

function classify(head: string, isCrossOrigin: boolean): string {
  if (RESIZE_OBSERVER_LOOP.test(head.trim().replace(LEADING_NOISE, ''))) {
    return FailKinds.benignResizeObserver;
  }

  return isCrossOrigin ? FailKinds.crossOriginScriptError : '';
}

function getRejectionStack(message: string): string {
  const stackIndex = message.indexOf(REJECTION_STACK_MARKER);

  if (stackIndex === -1) {
    return '';
  }

  const stackStart = stackIndex + REJECTION_STACK_MARKER.length;
  const repeatIndex = message.lastIndexOf(REPEAT_COUNT_MARKER);
  const hasRepeatCount = repeatIndex >= stackStart
    && DIGITS_ONLY.test(message.substring(repeatIndex + REPEAT_COUNT_MARKER.length));
  const stackEnd = hasRepeatCount ? repeatIndex : message.length;

  return message.substring(stackStart, stackEnd).trim();
}

export interface ErrorDetails {
  Source: string;
  Stack: string;
  FailKind: string;
  Line?: number;
  Col?: number;
}

/**
 * Derives the `Source` / `Line` / `Col` / `Stack` / `FailKind` columns from the flattened error
 * message produced by ErrorStub in `@bingads-webui/instrumentation-stubs`.
 *
 * Only the two message templates that ErrorStub actually emits are parsed. Anything unrecognised
 * yields empty columns rather than a guess - a wrong value in a telemetry column is worse than a
 * null one, because a DRI will act on it.
 *
 * TODO: remove this parsing once instrumentation-stubs emits source/line/col/stack structurally.
 * @param {InstrumentationLog} rawLog - the raw log being formatted
 * @returns {ErrorDetails} the columns to merge onto the formatted log
 */
export function getErrorDetails(rawLog: InstrumentationLog): ErrorDetails {
  const details: ErrorDetails = { Source: '', Stack: '', FailKind: '' };
  const { api, message } = rawLog;

  if (typeof message !== 'string' || (api !== ON_ERROR_API && api !== ON_REJECTION_API)) {
    return details;
  }

  if (api === ON_REJECTION_API) {
    details.Stack = scrubTokens(getRejectionStack(message)).substring(0, MAX_STACK_LENGTH);
    return details;
  }

  const head = getMessageHead(message);
  const segment = getScriptSegment(message);
  const location = segment === undefined ? undefined : parseScriptSegment(segment);
  const isCrossOrigin = isCrossOriginError(head, location);

  details.FailKind = classify(head, isCrossOrigin);

  if (!location || isCrossOrigin) {
    return details;
  }

  details.Source = scrubTokens(location.source);
  details.Stack = scrubTokens(location.stack).substring(0, MAX_STACK_LENGTH);

  if (location.line !== undefined) {
    details.Line = location.line;
  }
  if (location.col !== undefined) {
    details.Col = location.col;
  }

  return details;
}
